import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from "axios";
import Moment from 'react-moment';
import { Link, navigate } from 'raviger';
import { Button, Card, Col, Collapse, ListGroup, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft,
  faPrescription,
  faChevronCircleUp,
  faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
  faSquareChevronRight,
  faSquareChevronLeft,
  faChevronCircleDown,
  faCalendarExclamation,
  faVial,
  faScalpelLineDashed,
  faPrescriptionBottlePill,
  faChevronDoubleDown,
  faChevronDoubleUp
} from '@fortawesome/pro-solid-svg-icons';
import dayjs from 'dayjs';
import Timeline, {
  TimelineHeaders,
  TimelineMarkers,
  CustomMarker,
  DateHeader
} from 'react-calendar-timeline';
// import moment from 'moment';
import Select from 'react-select';
import DiagnosticResultForm from '../DiagnosticResultForm';
import ProcedureResultForm from '../ProcedureResultForm';
import TreatmentRequestForm from '../TreatmentRequestForm';
import LoadingLink from '../../components/LoadingLink';
import ActionsDropdown from '../../components/ActionsDropdown';
import { titleCase } from '../../components/Utils';
import { DateRangePicker } from '../../components/Form';
import { AuthContext } from "../../accounts/AccountsReducer";
import { SystemErrorContext } from '../../components/SystemError';

const customStyles = {
  // For the select it self, not the options of the select
  control: (styles, { isDisabled}) => {
    return {
      ...styles,
      color: '#FFF',
      cursor: isDisabled ? 'not-allowed' : 'default',
      backgroundColor: isDisabled ? '#DFDDDD' : 'white',
      height: 35,
      minHeight: 35
    }
  },
  option: provided => ({
    ...provided,
    color: 'black'
  }),
  singleValue: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? '#595959' : 'black'
  }),
  menu: provided  => ({ ...provided, zIndex: 9999 })
};

function PatientCalendarView(props) {

  const renderGroup = ({ group }) => {
    return (
      <div key={group.id} style={{whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}} >
        <Link href={group.vet_type === "treatment_plan" ? ("/" + props.organization + "/" + props.incident + "/vet/treatment/" + group.object_id) : group.vet_type === 'diagnostic' ? ("/" + props.organization + "/" + props.incident + "/vet/diagnosticresult/edit/" + group.object_id) : ("/" + props.organization + "/" + props.incident + "/vet/procedureresult/edit/" + group.object_id)} className="underline-link" title={group.title + (group.is_overdue ? " !" : "")} style={{color:"white", textDecoration: "none"}}>
          {group.title}
        </Link>
        {group.is_overdue ?
          <OverlayTrigger
            key={"overdue"}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-overdue`}>
                This veterinary task is overdue.
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faCalendarExclamation} style={{ color: "#ff4c4c" }} className="ms-1" />
          </OverlayTrigger>
        : ""}
        {group.is_complete ?
          <OverlayTrigger
            key={"overdue"}
            placement="top"
            overlay={
              <Tooltip id={`tooltip-overdue`}>
                This veterinary task is complete.
              </Tooltip>
            }
          >
            <FontAwesomeIcon icon={faCalendarCheck} style={{ color: "#008000" }} className="ms-1" />
          </OverlayTrigger>
        : ""}
      </div>
    );
  };

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({id:'', exams:[], diagnostic_objects:[], procedure_objects:[], discharge_summaries:[], patient:null, vet_requests:[], pending:[], open: '', images: [], diagnosis: '', other_diagnosis:'', medical_plan:'', medical_notes:[], treatment_requests:[], animal_object: {id:'', name:'', species:'', category:'', sex:'', age:'', fixed:'', pcolor:'', scolor:'', medical_notes:'', shelter_object:{}, room_name:'', owners:[], owner_contacts:[]}});
  const [isLoading, setIsLoading] = useState(true);
  const [calendarData, setCalendarData] = useState([]);
  const [groupData, setGroupData] = useState([]);
  const [currentDate, setCurrentDate] = useState(props.currentDate?.toDate() || dayjs().toDate());
  const [showCalendar, setShowCalendar] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [vetType, setVetType] = useState(null);
  const [showTreatmentRequestModal, setShowTreatmentRequestModal] = useState(false);
  const [submitTreatmentRequestForm, setSubmitTreatmentRequestForm] = useState(false);
  const [selectedTreatmentRequest, setSelectedTreatmentRequest] = useState(null);
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);
  const [submitDiagnosticForm, setSubmitDiagnosticForm] = useState(false);
  const [selectedDiagnostic, setSelectedDiagnostic] = useState(null);
  const [showProcedureModal, setShowProcedureModal] = useState(false);
  const [submitProcedureForm, setSubmitProcedureForm] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);

  const lastItemEndTimes = useMemo(() => {
    const mappings = {};
    groupData.forEach(group => {
      const groupItems = calendarData.filter(item => Number(item.group) === Number(group.id));
      if (groupItems.length > 0) {
        // Get the maximum end_time in this group
        mappings[group.id] = Math.max(...groupItems.map(i => i.end_time));
      } else {
        // If the group has no items, start coloring from the very beginning
        mappings[group.id] = dayjs(currentDate).startOf('day').valueOf();
      }
    });
    return mappings;
  }, [groupData, calendarData]);

  const firstItemStartTimes = useMemo(() => {
    const mappings = {};
    groupData.forEach(group => {
      const groupItems = calendarData.filter(item => Number(item.group) === Number(group.id));
      if (groupItems.length > 0) {
        // Get the min start_time in this group
        mappings[group.id] = Math.min(...groupItems.map(i => i.start_time));
      } else {
        // If the group has no items, start coloring from the very beginning
        mappings[group.id] = dayjs(currentDate).startOf('day').valueOf();
      }
    });
    return mappings;
  }, [groupData, calendarData]);

  const handleItemClick = (itemId, e, time) => {
    let item_data = calendarData.filter(item => String(item.id) === String(itemId))[0];
    if (itemId.includes("treatment_request")) {
      setSelectedTreatmentRequest(item_data.object_id)
      setShowTreatmentRequestModal(item_data.name)
    }
    if (itemId.includes("diagnostic")) {
      setSelectedDiagnostic(item_data.object_id)
      setShowDiagnosticModal(item_data.name)
    }
    if (itemId.includes("procedure")) {
      setSelectedProcedure(item_data.object_id)
      setShowProcedureModal(item_data.name)
    }
  };

  const [canvasWidth, setCanvasWidth] = useState(0);

  // Function to calculate exact width of the scrollable canvas container
  const updateCanvasWidth = (canvasTimeStart, canvasTimeEnd) => {
    const scrollContainer = document.querySelector('.rct-scroll');
    if (scrollContainer) {
      // This retrieves the actual width of the canvas currently rendered
      setCanvasWidth(scrollContainer.clientWidth);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      updateCanvasWidth();
    }, 1000)

    // Listen for resize changes affecting the timeline canvas layout
    window.addEventListener('resize', updateCanvasWidth);
    return () => window.removeEventListener('resize', updateCanvasWidth);
  }, []);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchMedRecordData = async () => {
      // Fetch MedicalRecord details data.
      await axios.get('/vet/api/medrecord/' + props.id + '/?incident=' + state.incident.id, {
          cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let group_data = [];
          let calendar_data = [];
          let id_tracker = 0;
          response.data.treatment_plans.forEach(treatment => {
            let is_overdue = false;
            let is_complete = false;
            treatment.treatment_requests.forEach(treatment_request => {
              calendar_data.push({name:treatment.description, id:'treatment_request' + treatment_request.id, object_id:treatment_request.id, group:id_tracker, title:dayjs(treatment_request.suggested_admin_time).format('HH:mm'), start_time: dayjs(treatment_request.suggested_admin_time), end_time: dayjs(treatment_request.suggested_admin_time).add(1, 'hour'),
                itemProps: {
                  style: {
                    background: treatment_request.not_administered ? 'gray' : treatment_request.actual_admin_time ? '#008000' : dayjs(treatment_request.suggested_admin_time).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                    color: 'white',       // Text color
                    borderColor: 'black'
                  }
                }
              });
              if (!treatment_request.not_administered && !treatment_request.actual_admin_time && dayjs(treatment_request.suggested_admin_time).isBefore(dayjs())) {
                is_overdue = true;
              }
            });
            if (treatment.treatment_requests.length === treatment.treatment_requests.filter(tr => tr.actual_admin_time || tr.not_administered).length) {
              is_complete = true;
            }
            group_data.push({id:id_tracker, object_id:treatment.id, title:treatment.description, vet_type:'treatment_plan', medical_record:treatment.medical_record, is_overdue:is_overdue, is_complete:is_complete});
            id_tracker = id_tracker + 1;
          });

          response.data.diagnostic_objects.forEach(diagnostic => {
            let is_overdue = false;
            let is_complete = false;
            if (!diagnostic.result && dayjs(diagnostic.open).isBefore(dayjs())) {
              is_overdue = true;
            }
            if (diagnostic.result) {
              is_complete = true;
            }
            calendar_data.push({name:diagnostic.name, id:'diagnostic' + diagnostic.id, object_id:diagnostic.id, group:id_tracker, title:dayjs(diagnostic.open).format('HH:mm'), start_time: dayjs(diagnostic.open), end_time: dayjs(diagnostic.open).add(1, 'hour'),
              itemProps: {
                style: {
                  background: diagnostic.result ? '#008000' : dayjs(diagnostic.open).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                  color: 'white',       // Text color
                  borderColor: 'black'
                }
              }
            });
            group_data.push({id:id_tracker, object_id:diagnostic.id, title:diagnostic.name === 'Other' ? diagnostic.other_name : diagnostic.name, vet_type:'diagnostic', medical_record:diagnostic.medical_record, is_overdue:is_overdue, is_complete:is_complete});
            id_tracker = id_tracker + 1;
          });

          response.data.procedure_objects.forEach(procedure => {
            let is_overdue = false;
            let is_complete = false;
            if (!procedure.complete && dayjs(procedure.open).isBefore(dayjs())) {
              is_overdue = true;
            }
            if (procedure.complete) {
              is_complete = true;
            }
            calendar_data.push({name:procedure.name, id:'procedure' + procedure.id, object_id:procedure.id, group:id_tracker, title:dayjs(procedure.open).format('HH:mm'), start_time: dayjs(procedure.open), end_time: dayjs(procedure.open).add(1, 'hour'),
              itemProps: {
                style: {
                  background: procedure.complete ? '#008000' : dayjs(procedure.open).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                  color: 'white',       // Text color
                  borderColor: 'black'
                }
              }
            });
            group_data.push({id:id_tracker, object_id:procedure.id, title:procedure.name === 'Other' ? procedure.other_name : procedure.name, vet_type:'procedure', medical_record:procedure.medical_record, is_overdue:is_overdue, is_complete:is_complete});
            id_tracker = id_tracker + 1;
          });

          group_data = [...group_data].sort((a, b) => a.title.localeCompare(b.title));

          setCalendarData(calendar_data);
          setGroupData(group_data);
          setData(response.data);
        }
      })
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
        return null;
      })
      .finally(() => setIsLoading(false));
    };
    fetchMedRecordData();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [props.id, props.incident]);

  return (
    <>
    <div className="row">
      <div className="col-12 d-flex">
        <Card className="border rounded" style={{width:"100%"}}>
          <Card.Body style={{marginTop:"-10px"}}>
            <div className="d-flex justify-content-between" style={{marginBottom:"-3px"}}>
              <h4 className="h4 mb-0 pb-0 pt-2 w-100">
                {props.selectedAnimal ? <OverlayTrigger
                  key={"calendar-view"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-calendar-view`}>
                      Return to Vet Calendar View.
                    </Tooltip>
                  }
                ><span id="back" style={{cursor:'pointer'}} onClick={(e) => {props.setSelectedAnimal(null)}} className="me-2"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span></OverlayTrigger> : ""}
                {props.selectedAnimal ? <Link href={"/" + props.organization + "/" + props.incident + "/vet/medrecord/" + props.id} className="underline-link" style={{color:"white", textDecoration: "none"}}>{'A#' + props.selectedAnimal.id_for_incident + ' - ' + (props.selectedAnimal.name || "Unknown Name") + " - " + titleCase(props.selectedAnimal.species_string_singular)}</Link> : "Treatments/Diagnostics/Procedures"}
                <FontAwesomeIcon icon={faChevronCircleUp} hidden={!showCalendar} onClick={() => {setShowCalendar(false)}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                <FontAwesomeIcon icon={faChevronCircleDown} hidden={showCalendar} onClick={() => {setShowCalendar(true)}} className="ms-1" size="sm" style={{cursor:'pointer'}} inverse />
                <Button variant="outline-light" className="rounded float-end mr-auto" onClick={() => setShowFilters(!showFilters)} style={{height:"38px", color:"white", marginTop:"-5px"}}>Filters {showFilters ? <FontAwesomeIcon icon={faChevronDoubleUp} className="fa-move-up" size="sm" /> : <FontAwesomeIcon icon={faChevronDoubleDown} className="fa-move-up" size="sm" />}</Button>
              </h4>
              {(state.user.is_superuser || state.user.vet_perms) ? <ActionsDropdown variant="secondary" title="Actions" className="float-end ms-2" style={{marginTop:"1px"}}>
                <LoadingLink
                  href={"/" + props.organization + "/" + props.incident + "/vet/medrecord/" + data.id + "/treatment/new"}
                  // onClick={() => {setShowTreatmentRequestModal(true)}}
                  isLoading={isLoading}
                  className="d-block"label="Add Treatment"
                  disabled={data.exams.length === 0}
                  disabled_text="Patient must be examined first."
                >
                  <FontAwesomeIcon icon={faPrescriptionBottlePill} className="fas" inverse />
                </LoadingLink>
                <LoadingLink
                  href={"/" + props.organization + "/" + props.incident + "/vet/medrecord/" + data.id + "/diagnostics"}
                  // onClick={() => {setShowDiagnosticModal(true)}}
                  isLoading={isLoading}
                  className="d-block"
                  label="Order Diagnostics"
                  disabled={data.exams.length === 0}
                  disabled_text="Patient must be examined first."
                >
                  <FontAwesomeIcon icon={faVial} className="fas" style={{cursor:'pointer'}} transform='grow-2' inverse />
                </LoadingLink>
                <LoadingLink
                  href={"/" + props.organization + "/" + props.incident + "/vet/medrecord/" + data.id + "/procedures"}
                  // onClick={() => {setShowProcedureModal(true)}}
                  isLoading={isLoading}
                  className="d-block"
                  label="Order Procedures"
                  disabled={data.exams.length === 0}
                  disabled_text="Patient must be examined first."
                >
                  <FontAwesomeIcon icon={faScalpelLineDashed} className="fas" style={{cursor:'pointer'}} transform='grow-1' inverse />
                </LoadingLink>
              </ActionsDropdown> : ""}
            </div>
            <Collapse in={showFilters} className="mt-3">
              <div>
                <Card className="border rounded d-flex" style={{width:"100%"}}>
                  <Card.Body style={{marginTop:"-10px"}}>
                    <Row className="d-flex gx-2">
                      <Col xs={6}>
                        <label className="mb-2">Date Selection</label>
                        <DateRangePicker
                          name={`current_date`}
                          id={`current_date`}
                          mode="single"
                          style={{height:"35px"}}
                          data-enable-time={false}
                          clearable={'false'}
                          value={currentDate || null}
                          onChange={([date], dateStr) => {
                            setCurrentDate(date)
                          }}
                          disabled={data.isFetching}
                        />
                      </Col>
                      <Col xs={6}>
                        <label className="mb-2">Vet Task Type</label>
                        <Select
                          id="vet_task"
                          name="vet_task"
                          styles={customStyles}
                          onChange={(instance) => {
                            setVetType(instance === null ? '' : instance.value);
                          }}
                          isClearable={true}
                          isDisabled={data.isFetching}
                          options={[{value:'treatment_plan', label:'Treatments'}, {value:'diagnostic', label:'Diagnostics'}, {value:'procedure', label:'Procedures'}]}
                          placeholder="Filter by Vet Task Type..."
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>
            <Collapse in={showCalendar}>
              <div>
                <p className="phr" />
                <Timeline
                  groups={groupData.filter(group => vetType ? group.vet_type === vetType : group)}
                  items={calendarData}
                  onItemSelect={handleItemClick}
                  onItemClick={handleItemClick}
                  buffer={1}
                  canMove={false}
                  canResize={false}
                  canChangeGroup={false}
                  defaultTimeStart={dayjs(currentDate).startOf('day')}
                  defaultTimeEnd={dayjs(currentDate).endOf('day')}
                  visibleTimeStart={dayjs(currentDate).startOf('day')}
                  visibleTimeEnd={dayjs(currentDate).endOf('day')}
                  groupRenderer={renderGroup}
                  sidebarWidth={200}
                >
                  <TimelineHeaders>
                    <DateHeader
                      unit="day"
                      intervalRenderer={({ getIntervalProps, intervalContext }) => {
                        const { key, ...intervalProps } = getIntervalProps();
                        return (
                        <div key={key}>
                          <div {...intervalProps} className="" style={{
                            ...intervalProps.style,
                            display: 'flex',
                            justifyContent: 'flex-start', // Align content to the far left
                            paddingLeft: '2px',          // Add spacing from the edge
                          }}>
                            <OverlayTrigger
                              key={"previous-day"}
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-previous-day`}>
                                  Go to previous day.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faSquareChevronLeft} onClick={() => {setCurrentDate(dayjs(currentDate).add(-1, 'day').toDate())}} size="2x" style={{ cursor: 'pointer', marginTop:"0px", zIndex:"999" }} inverse />
                            </OverlayTrigger>
                          </div>
                          <div className="float-end" style={{
                            ...intervalProps.style,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'end',
                            cursor: 'pointer',          // Add spacing from the edge
                            paddingRight: '4px',
                          }}>
                            <OverlayTrigger
                              key={"next-day"}
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-next-day`}>
                                  Go to next day.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faSquareChevronRight} onClick={() => {setCurrentDate(dayjs(currentDate).add(1, 'day').toDate())}} size="2x" style={{ cursor:'pointer', marginTop:"0px", zIndex:"999" }} inverse />
                            </OverlayTrigger>
                          </div>
                          <span className="calendar-date-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor:"default" }}>
                            <span style={{marginTop:"4px"}}>{isLoading ? "Loading..." : dayjs(currentDate).format('dddd, MMMM D, YYYY')}</span>
                          </span>
                        </div>
                        );
                      }}
                    />
                    <DateHeader
                      unit="hour"
                      style={{marginLeft:"-1px"}}
                    />
                  </TimelineHeaders>
                  <TimelineMarkers>
                    {canvasWidth && groupData.map((group, index) => {
                      const lastEnd = lastItemEndTimes[group.id];
                      return (
                        <CustomMarker key={Math.random()} date={lastEnd}>
                          {({ styles }) => {
                            const totalCanvasDuration = dayjs(currentDate).endOf('day').valueOf() - dayjs(currentDate).startOf('day').valueOf();
                            const pxPerMs = canvasWidth / totalCanvasDuration;

                            // 2. Compute the distance in milliseconds from the marker to the end of the visible view
                            const durationToEndOfView = dayjs(currentDate).endOf('day').valueOf() - lastEnd;

                            // 3. Convert duration to pixels
                            const widthInPixels = durationToEndOfView * pxPerMs;

                            // Ensure we don't return negative widths if the marker is off-screen past the right side
                            const safeWidth = Math.max(0, widthInPixels);

                            // 2. Adjust styles to turn the marker vertical line into a block
                            const customStyles = {
                              ...styles,
                              backgroundColor: 'rgba(120, 116, 116, 0.65)', // Your empty background color
                              top: `${index * 30}px`,           // Align vertically with the row
                              height: `${30}px`,
                              width: `${safeWidth}px`,
                              pointerEvents: 'none',                   // Ensure clicks still pass through to canvas
                              zIndex: 0                                // Keep it underneath timeline items
                            };
                            return <div style={customStyles} />;
                          }}
                        </CustomMarker>
                      );
                    })}
                    {canvasWidth && groupData.map((group, index) => {
                      const lastEnd = lastItemEndTimes[group.id];
                      return (
                        <>
                        {dayjs(lastEnd).isBefore(dayjs(currentDate), 'day') ? <CustomMarker key={Math.random()} date={dayjs(currentDate).valueOf()}>
                          {({ styles }) => {
                            // 2. Adjust styles to turn the marker vertical line into a block
                            const customStyles = {
                              ...styles,
                              backgroundColor: 'rgba(120, 116, 116, 0.65)', // Your empty background color
                              top: `${index * 30}px`,           // Align vertically with the row
                              height: `${30}px`,
                              left: `0px`,
                              width: `${canvasWidth}px`,
                              pointerEvents: 'none',                   // Ensure clicks still pass through to canvas
                              zIndex: 0                                // Keep it underneath timeline items
                            };
                            return <div style={customStyles} />;
                          }}
                        </CustomMarker> : ""}
                        </>
                      );
                    })}
                    {canvasWidth && groupData.map((group, index) => {
                      const firstStart = dayjs(firstItemStartTimes[group.id]).valueOf();
                      
                      const totalCanvasDuration = dayjs(currentDate).endOf('day').valueOf() - dayjs(currentDate).startOf('day').valueOf();
                      const pxPerMs = canvasWidth / totalCanvasDuration;

                      // 2. Compute the distance in milliseconds from the marker to the end of the visible view
                      const durationToEndOfView = firstStart - dayjs(currentDate).startOf('day').valueOf();

                      // 3. Convert duration to pixels
                      const widthInPixels = durationToEndOfView * pxPerMs;

                      // Ensure we don't return negative widths if the marker is off-screen past the right side
                      const safeWidth = Math.max(0, widthInPixels);

                      return (
                        <CustomMarker key={Math.random()} date={dayjs(firstStart).startOf('day').valueOf()}>
                          {({ styles }) => {
                            const customStyles = {
                              ...styles,
                              backgroundColor: 'rgba(120, 116, 116, 0.65)', // Your empty background color
                              top: `${index * 30}px`,           // Align vertically with the row
                              height: `${30}px`,
                              width: `${safeWidth}px`,
                              pointerEvents: 'none',                   // Ensure clicks still pass through to canvas
                              zIndex: 0                                // Keep it underneath timeline items
                            };
                            return <div style={customStyles} />;
                          }}
                        </CustomMarker>
                      );
                    })}
                    {canvasWidth && groupData.map((group, index) => {
                      const firstStart = dayjs(firstItemStartTimes[group.id]).valueOf();
                      return (
                        <>
                        {dayjs(dayjs(currentDate)).isBefore(firstStart, 'day') ? <CustomMarker key={Math.random()} date={dayjs(currentDate).valueOf()}>
                          {({ styles }) => {
                            // 2. Adjust styles to turn the marker vertical line into a block
                            const customStyles = {
                              ...styles,
                              backgroundColor: 'rgba(120, 116, 116, 0.65)', // Your empty background color
                              top: `${index * 30}px`,           // Align vertically with the row
                              height: `${30}px`,
                              left: `0px`,
                              width: `${canvasWidth}px`,
                              pointerEvents: 'none',                   // Ensure clicks still pass through to canvas
                              zIndex: 0                                // Keep it underneath timeline items
                            };
                            return <div style={customStyles} />;
                          }}
                        </CustomMarker> : ""}
                        </>
                      );
                    })}
                  </TimelineMarkers>
                </Timeline>
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      </div>
    </div>
    <Modal show={showTreatmentRequestModal} dialogClassName="semi-wide-modal" onHide={() => {setShowTreatmentRequestModal(false)}}>
      <Modal.Header closeButton>
        <Modal.Title>{showTreatmentRequestModal}</Modal.Title>
      </Modal.Header>
        <TreatmentRequestForm id={selectedTreatmentRequest} modal={true} setParentData={setCalendarData} submit_form={submitTreatmentRequestForm} setResetSubmit={setSubmitTreatmentRequestForm} incident={props.incident} organization={props.organization} setShowModal={setShowTreatmentRequestModal} />
      <Modal.Footer>
        <Button variant="primary" onClick={() => {setSubmitTreatmentRequestForm(true)}}>Save</Button>
        <Button variant="secondary" onClick={() => {setShowTreatmentRequestModal(false)}}>Close</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showDiagnosticModal} dialogClassName="semi-wide-modal" onHide={() => {setShowDiagnosticModal(false)}}>
      <Modal.Header closeButton>
        <Modal.Title>{showDiagnosticModal}</Modal.Title>
      </Modal.Header>
        <DiagnosticResultForm id={selectedDiagnostic} modal={true} setParentData={setCalendarData} submit_form={submitDiagnosticForm} setResetSubmit={setSubmitDiagnosticForm} incident={props.incident} organization={props.organization} setShowModal={setShowDiagnosticModal} />
      <Modal.Footer>
        <Button variant="primary" onClick={() => {setSubmitDiagnosticForm(true)}}>Save</Button>
        <Button variant="secondary" onClick={() => {setShowDiagnosticModal(false)}}>Close</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showProcedureModal} dialogClassName="semi-wide-modal" onHide={() => {setShowProcedureModal(false)}}>
      <Modal.Header closeButton>
        <Modal.Title>{showProcedureModal}</Modal.Title>
      </Modal.Header>
        <ProcedureResultForm id={selectedProcedure} modal={true} setParentData={setCalendarData} submit_form={submitProcedureForm} setResetSubmit={setSubmitProcedureForm} incident={props.incident} organization={props.organization} setShowModal={setShowProcedureModal} />
      <Modal.Footer>
        <Button variant="primary" onClick={() => {setSubmitProcedureForm(true)}}>Save</Button>
        <Button variant="secondary" onClick={() => {setShowProcedureModal(false)}}>Close</Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default PatientCalendarView;
